import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  borrarTexcumarMantenimiento,
  crearTexcumarMantenimiento,
  getTexcumarMantenimiento,
} from '../../utils/api';
import { formatDateTime } from './lib/dates';
import { baseLabel, roomName } from './lib/format';

/* --------------------------------------------------------------------- */
/* Declaración de mantenimientos                                          */
/*                                                                        */
/* Limpiar una sonda produce lecturas que no describen el agua: la sonda   */
/* está en la mano de alguien. Declarando la ventana, lo que cae dentro    */
/* deja de contar para el semáforo, los promedios y el tiempo fuera de     */
/* banda, y la sala aparece «en mantenimiento» en vez de en rojo o como    */
/* caída.                                                                  */
/*                                                                        */
/* Lo que NO hace es borrar: las lecturas siguen enteras en la tabla de    */
/* detalle y en el Excel. Silenciar una alarma tiene que dejar rastro, y   */
/* por eso cada ventana guarda quién la declaró y cuándo.                   */
/* --------------------------------------------------------------------- */

const MOTIVOS = [
  'Limpieza de sonda',
  'Calibración',
  'Cambio de sensor',
  'Corte de energía programado',
  'Mantenimiento general',
];

// `datetime-local` habla en hora local sin zona; la API quiere ISO en UTC.
function aCampo(fecha) {
  const local = new Date(fecha.getTime() - fecha.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function aIso(valor) {
  if (!valor) return '';
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime()) ? '' : fecha.toISOString();
}

export function MantenimientoPanel({ isOpen, onClose, rooms, seleccionInicial, onSaved }) {
  const ahora = useMemo(() => new Date(), [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const [series, setSeries] = useState([]);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [ventanas, setVentanas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const recargar = useCallback(() => {
    getTexcumarMantenimiento()
      .then((resultado) => setVentanas(resultado.data || []))
      .catch(() => setVentanas([]));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    /* Media hora hacia atrás y hasta ahora: es lo que dura una limpieza y lo
     * que alguien acaba de hacer cuando abre esto. Quien necesite otra cosa lo
     * corrige, pero el caso corriente no pide teclear ninguna fecha. */
    setDesde(aCampo(new Date(ahora.getTime() - 30 * 60000)));
    setHasta(aCampo(ahora));
    setSeries(seleccionInicial ? [seleccionInicial] : []);
    setMotivo(MOTIVOS[0]);
    setError(null);
    recargar();
  }, [isOpen, ahora, seleccionInicial, recargar]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const alPulsar = (evento) => { if (evento.key === 'Escape') onClose(); };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [isOpen, onClose]);

  /* Por base y por catálogo, no por criticidad: aquí se busca una sala por su
   * nombre —la que se acaba de limpiar— y el orden tiene que ser el mismo cada
   * vez. En el panel de estado manda la urgencia; en esta lista, la memoria. */
  const salas = useMemo(() => (rooms || [])
    .filter((room) => room.tipo !== 'referencia')
    .slice()
    .sort((a, b) => (
      String(a.base_code).localeCompare(String(b.base_code))
      || (a.orden ?? 999) - (b.orden ?? 999)
      || String(a.serie).localeCompare(String(b.serie))
    )), [rooms]);

  const alternar = (serie) => setSeries((actual) => (
    actual.includes(serie) ? actual.filter((x) => x !== serie) : [...actual, serie]
  ));

  const guardar = () => {
    setError(null);

    if (!series.length) return setError('Elija al menos una sala.');
    const desdeIso = aIso(desde);
    const hastaIso = aIso(hasta);
    if (!desdeIso || !hastaIso) return setError('Las dos fechas tienen que ser válidas.');
    if (new Date(desdeIso) >= new Date(hastaIso)) return setError('El inicio tiene que ir antes del fin.');
    if ((new Date(hastaIso) - new Date(desdeIso)) / 3600000 > 24) {
      return setError('Una ventana no puede cubrir más de 24 horas.');
    }

    setGuardando(true);
    return crearTexcumarMantenimiento({
      series, desde: desdeIso, hasta: hastaIso, motivo: motivo.trim(),
    })
      .then(() => {
        recargar();
        onSaved();
        onClose();
      })
      .catch((err) => setError(err.message || 'No se pudo guardar.'))
      .finally(() => setGuardando(false));
  };

  const borrar = (id) => borrarTexcumarMantenimiento(id)
    .then(() => { recargar(); onSaved(); })
    .catch((err) => setError(err.message || 'No se pudo retirar.'));

  if (!isOpen) return null;

  const nombrePorSerie = new Map(salas.map((room) => [room.serie, room]));

  return (
    <div className="tx-config-overlay" role="dialog" aria-modal="true" aria-label="Registrar mantenimiento">
      <div className="tx-config-panel">
        <header className="tx-config-header">
          <div>
            <h2>Registrar mantenimiento</h2>
            <p>
              Lo que ocurra dentro de la ventana deja de contar para el semáforo y los
              promedios. Las lecturas no se borran: siguen en el detalle y en el Excel.
            </p>
          </div>
          <button type="button" className="tx-secondary" onClick={onClose}>Cerrar</button>
        </header>

        {error && <p className="tx-config-error">{error}</p>}

        <div className="tx-mant-form">
          <fieldset className="tx-mant-salas">
            <legend>Salas ({series.length} elegidas)</legend>
            <div className="tx-mant-lista">
              {salas.map((room) => (
                <label key={room.serie} className={series.includes(room.serie) ? 'is-active' : ''}>
                  <input
                    type="checkbox"
                    checked={series.includes(room.serie)}
                    onChange={() => alternar(room.serie)}
                  />
                  <span>
                    <em>{baseLabel(room.base_code)}</em>
                    {roomName(room)}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="tx-mant-campos">
            <label htmlFor="mant-desde">
              Desde
              <input
                id="mant-desde"
                type="datetime-local"
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
              />
            </label>
            <label htmlFor="mant-hasta">
              Hasta
              <input
                id="mant-hasta"
                type="datetime-local"
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
              />
            </label>
            <label htmlFor="mant-motivo">
              Motivo
              <input
                id="mant-motivo"
                type="text"
                list="mant-motivos"
                maxLength={120}
                value={motivo}
                onChange={(evento) => setMotivo(evento.target.value)}
              />
              <datalist id="mant-motivos">
                {MOTIVOS.map((item) => <option key={item} value={item} />)}
              </datalist>
            </label>
            <button type="button" className="tx-primary" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando…' : 'Registrar'}
            </button>
          </div>
        </div>

        <h3 className="tx-mant-titulo">Ventanas declaradas</h3>
        {ventanas.length === 0 ? (
          <p className="tx-mant-vacio">Todavía no hay ninguna.</p>
        ) : (
          <ul className="tx-mant-declaradas">
            {ventanas.slice(0, 20).map((ventana) => (
              <li key={ventana.id}>
                <div>
                  <strong>{ventana.motivo}</strong>
                  <em>
                    {ventana.series
                      .map((serie) => {
                        const room = nombrePorSerie.get(serie);
                        return room ? `${baseLabel(room.base_code)} · ${roomName(room)}` : serie;
                      })
                      .join('   ')}
                  </em>
                  <small>
                    {formatDateTime(ventana.desde)} — {formatDateTime(ventana.hasta)}
                    {ventana.autor ? ` · ${ventana.autor}` : ''}
                  </small>
                </div>
                <button type="button" className="tx-link-button" onClick={() => borrar(ventana.id)}>
                  Retirar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default MantenimientoPanel;
